import { memo, useCallback } from 'react';
import { Handle, Position, useEdges, type NodeProps } from '@xyflow/react';
import {
  Box, Button, Typography, IconButton, Switch, FormControlLabel, CircularProgress,
} from '@mui/material';
import { Delete, MergeType, VolumeOff, VolumeUp } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { HANDLES, useCanvasStore } from '../useCanvasStore';
import { studioService } from '../../../../services/studio.service';

export interface ConcatNodeData {
  label: string;
  stripAudio: boolean;
  isProcessing?: boolean;
  resultUrl?: string;
  resultGcsPath?: string;
  durationMs?: number;
  [key: string]: unknown;
}

function ConcatNode({ id, data }: NodeProps & { data: ConcatNodeData }) {
  const updateNodeData = useCanvasStore((s) => s.updateNodeData);
  const removeNode = useCanvasStore((s) => s.removeNode);
  const { enqueueSnackbar } = useSnackbar();
  const edges = useEdges();

  // Find all video nodes connected to this concat node's inputs
  const connectedVideoEdges = edges.filter(e => e.target === id && e.targetHandle === 'concat-in');
  const nodes = useCanvasStore((s) => s.nodes);
  const connectedVideos = connectedVideoEdges.map(e => {
    const node = nodes.find(n => n.id === e.source);
    const nd = node?.data as any;
    return {
      nodeId: e.source,
      name: nd?.prompt?.substring(0, 30) || node?.id?.substring(0, 15) || '?',
      jobId: nd?.jobId,
      status: nd?.status,
      hasVideo: nd?.status === 'completed' && !!nd?.jobId,
    };
  });

  const allReady = connectedVideos.length >= 2 && connectedVideos.every(v => v.hasVideo);

  const handleConcat = useCallback(async () => {
    const jobIds = connectedVideos.filter(v => v.jobId).map(v => v.jobId);
    if (jobIds.length < 2) {
      enqueueSnackbar('Connect at least 2 completed video nodes', { variant: 'warning' });
      return;
    }
    updateNodeData(id, { isProcessing: true });
    try {
      const result = await studioService.concatVideos(jobIds, data.stripAudio);
      updateNodeData(id, {
        isProcessing: false,
        resultUrl: result.signedUrl,
        resultGcsPath: result.gcsPath,
        durationMs: result.durationMs,
      });
      enqueueSnackbar(`Videos concatenated! ${(result.durationMs / 1000).toFixed(1)}s`, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar((err as Error).message, { variant: 'error' });
      updateNodeData(id, { isProcessing: false });
    }
  }, [id, data, connectedVideos, updateNodeData, enqueueSnackbar]);

  return (
    <Box sx={{
      bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
      borderRadius: 2, width: 260, overflow: 'hidden',
    }}>
      {/* Input handle — accepts multiple video connections */}
      <Handle
        type="target"
        position={Position.Left}
        id="concat-in"
        style={{ background: '#f48fb1', width: 10, height: 10, top: '50%' }}
      />

      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 1.5, py: 0.75, bgcolor: '#7b1fa2', color: 'white',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <MergeType sx={{ fontSize: 16 }} />
          <Typography variant="caption" fontWeight={600}>Concat Videos</Typography>
        </Box>
        <IconButton size="small" onClick={() => removeNode(id)} sx={{ color: 'inherit' }}>
          <Delete sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Connected videos list */}
        <Typography variant="caption" color="text.secondary">
          {connectedVideos.length === 0 ? 'Connect video nodes to the left input' :
            `${connectedVideos.length} videos connected`}
        </Typography>

        {connectedVideos.map((v, i) => (
          <Box key={v.nodeId} sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            px: 1, py: 0.3, bgcolor: 'action.hover', borderRadius: 1, fontSize: '0.7rem',
          }}>
            <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 16 }}>{i + 1}.</Typography>
            <Typography variant="caption" noWrap sx={{ flex: 1 }}>{v.name}</Typography>
            <Typography variant="caption" color={v.hasVideo ? 'success.main' : 'text.disabled'}>
              {v.hasVideo ? 'Ready' : v.status || 'No video'}
            </Typography>
          </Box>
        ))}

        {/* Strip audio toggle */}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={data.stripAudio}
              onChange={(e) => updateNodeData(id, { stripAudio: e.target.checked })}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {data.stripAudio ? <VolumeOff sx={{ fontSize: 14 }} /> : <VolumeUp sx={{ fontSize: 14 }} />}
              <Typography variant="caption">
                {data.stripAudio ? 'Strip audio' : 'Keep audio'}
              </Typography>
            </Box>
          }
          sx={{ mx: 0 }}
        />

        {/* Concat button */}
        <Button
          variant="contained"
          size="small"
          onClick={handleConcat}
          disabled={data.isProcessing || !allReady}
          startIcon={data.isProcessing ? <CircularProgress size={14} /> : <MergeType sx={{ fontSize: 14 }} />}
          fullWidth
          sx={{ bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#6a1b9a' } }}
        >
          {data.isProcessing ? 'Concatenating…' : `Concat ${connectedVideos.length} Videos`}
        </Button>

        {/* Result */}
        {data.resultUrl && (
          <Box sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            <video
              src={data.resultUrl}
              controls
              style={{ width: '100%', display: 'block' }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
              {data.durationMs ? `${(data.durationMs / 1000).toFixed(1)}s` : ''}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Output handle */}
      {data.resultUrl && (
        <Handle
          type="source"
          position={Position.Right}
          id={HANDLES.videoOut}
          style={{ background: '#f48fb1', width: 10, height: 10 }}
        />
      )}
    </Box>
  );
}

export default memo(ConcatNode);
